import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import serverData from '@/helper/server-data'
import { useEnvironmentCheck } from '@/composables/useEnvironmentCheck'

const { isBeta } = useEnvironmentCheck()

const SENSITIVE_FIELDS = [
  'msg', 'email', 'b', 'nachname', 'db_pass', 'google_api_key',
  'bounce_imap_pass', 'password', 'passwd', 'pass', 'pw',
]

function scrubData (data) {
  if (!data || typeof data !== 'object') return data

  if (Array.isArray(data)) {
    return data.map(scrubData)
  }

  const scrubbed = {}
  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase()

    if (SENSITIVE_FIELDS.includes(lowerKey)) {
      // If it's in the sensitive list, mask it completely
      scrubbed[key] = '[Filtered]'
    } else {
      // Otherwise, pass it through and check nested objects
      scrubbed[key] = scrubData(value)
    }
  }
  return scrubbed
}

if (serverData.ravenConfig) {
  // Initialize Sentry
  Sentry.init({
    Vue,
    attachProps: true,
    logErrors: true,
    sendDefaultPii: false,
    release: serverData.version || 'unknown',
    dsn: serverData.ravenConfig,
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),
    ],
    attachStacktrace: true,
    tracesSampleRate: isBeta ? 1.0 : 0.01,
    replaysSessionSampleRate: isBeta ? 1 : 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend (event) {
      if (event.request) {
        event.request = scrubData(event.request)
      }
      if (event.extra) {
        event.extra = scrubData(event.extra)
      }
      if (event.contexts) {
        event.contexts = scrubData(event.contexts)
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = scrubData(event.breadcrumbs)
      }
      return event
    },
  })

  // Set user context
  if (serverData?.user) {
    Sentry.setUser({
      id: serverData.user.id,
      username: serverData.user.firstname,
    })
  }
}

export function captureError (error) {
  if (typeof error === 'string') {
    error = new Error(error)
  }
  console.error(error)
  Sentry.captureException(error)
  return error
}

export function captureRequestError (error, { path, options, attempt }) {
  Sentry.withScope((scope) => {
    if (error.name === 'AbortError') {
      scope.setExtra('path', path)
      scope.setExtra('options', options)
      scope.setExtra('attempt', attempt)
      Sentry.captureException(new Error('Request timeout'))
      return
    }

    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      scope.setExtra('path', path)
      scope.setExtra('options', options)
      scope.setExtra('attempt', attempt)
      scope.setTag('device', /iPhone|iPad|iPod|Safari/i.test(navigator.userAgent) ? 'apple' : 'other')
      Sentry.captureException(error)
      return
    }

    // Add any other error details
    scope.setExtra('path', path)
    scope.setExtra('options', options)
    scope.setExtra('attempt', attempt)
    scope.setExtra('errorType', error.constructor.name)
    if (error.code) scope.setExtra('statusCode', error.code)
    if (error.statusText) scope.setExtra('statusText', error.statusText)
    if (error.jsonContent) scope.setExtra('jsonContent', error.jsonContent)

    Sentry.captureException(error)
  })
}
